import React, { useState, useEffect } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, FileText, Calculator, Upload, MessageCircle,
  BookOpen, CreditCard, CalendarDays, Bell, Settings, LogOut,
  Users, Shield, Briefcase, ClipboardList, BookOpenCheck, UserCog,
  ShieldCheck, Receipt, AlertTriangle, Activity, Flag, Search,
  ChevronLeft, ChevronRight,
} from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import AIAssistantWidget from "@/components/AIAssistantWidget";
import NotificationsBell from "@/components/NotificationsBell";
import CommandPalette from "@/components/business/CommandPalette";
import ThemeToggle from "@/components/ThemeToggle";
import MobileFAB from "@/components/MobileFAB";

const taxpayerGroups = [
  {
    items: [
      { to: "/dashboard", label: "Workspace", icon: LayoutDashboard, end: true },
      { to: "/filing", label: "File Return", icon: FileText },
      { to: "/calculator", label: "Tax Calculator", icon: Calculator },
    ],
  },
  {
    label: "Documents & Data",
    items: [
      { to: "/documents", label: "Documents", icon: Upload },
      { to: "/ais", label: "AIS Review", icon: ShieldCheck },
      { to: "/notices", label: "Notices", icon: AlertTriangle },
      { to: "/calendar", label: "Calendar", icon: CalendarDays },
    ],
  },
  {
    label: "Tools",
    items: [
      { to: "/knowledge", label: "Research", icon: BookOpen },
      { to: "/ai", label: "Advisor", icon: MessageCircle },
      { to: "/appointments", label: "Book a CA", icon: CalendarDays },
      { to: "/plans", label: "Plans & Billing", icon: CreditCard },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/notifications", label: "Notifications", icon: Bell },
      { to: "/profile", label: "Profile", icon: Settings },
    ],
  },
];

const caGroups = [
  {
    items: [
      { to: "/ca-portal", label: "Workspace", icon: LayoutDashboard, end: true },
      { to: "/practice/clients", label: "Clients", icon: Users },
      { to: "/ca-portal/filings", label: "Filings", icon: FileText },
      { to: "/approvals", label: "Review Center", icon: ShieldCheck },
    ],
  },
  {
    label: "Practice",
    items: [
      { to: "/practice", label: "Practice", icon: Briefcase, end: true },
      { to: "/practice/tasks", label: "Task Board", icon: ClipboardList },
      { to: "/practice/workflows", label: "Workflows", icon: BookOpenCheck },
      { to: "/practice/staff", label: "Staff", icon: UserCog },
    ],
  },
  {
    label: "Modules",
    items: [
      { to: "/tds", label: "TDS", icon: Receipt },
      { to: "/ais", label: "AIS Ingestion", icon: ShieldCheck },
      { to: "/notices", label: "Notices", icon: AlertTriangle },
      { to: "/calendar", label: "Calendar", icon: CalendarDays },
    ],
  },
  {
    label: "Tools",
    items: [
      { to: "/knowledge", label: "Research", icon: BookOpen },
      { to: "/ai", label: "Advisor", icon: MessageCircle },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/audit", label: "Audit Trail", icon: Shield },
      { to: "/profile", label: "Profile", icon: Settings },
    ],
  },
];

const adminGroups = [
  {
    items: [
      { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
      { to: "/admin-governance", label: "Governance", icon: Activity },
      { to: "/admin-flags", label: "Feature Flags", icon: Flag },
    ],
  },
  {
    label: "Management",
    items: [
      { to: "/admin/users", label: "Users", icon: Users },
      { to: "/admin/filings", label: "Filings", icon: FileText },
      { to: "/admin/payments", label: "Payments", icon: CreditCard },
    ],
  },
  {
    label: "System",
    items: [
      { to: "/audit", label: "Audit Trail", icon: Shield },
    ],
  },
  {
    label: "Account",
    items: [
      { to: "/profile", label: "Profile", icon: Settings },
    ],
  },
];

const taxpayerMobileTabs = [
  { to: "/dashboard", label: "Workspace", icon: LayoutDashboard, end: true },
  { to: "/filing", label: "File", icon: FileText },
  { to: "/calculator", label: "Calculator", icon: Calculator },
  { to: "/ai", label: "Advisor", icon: MessageCircle },
  { to: "/profile", label: "Profile", icon: Settings },
];

const caMobileTabs = [
  { to: "/ca-portal", label: "Workspace", icon: LayoutDashboard, end: true },
  { to: "/practice/clients", label: "Clients", icon: Users },
  { to: "/ca-portal/filings", label: "Filings", icon: FileText },
  { to: "/approvals", label: "Review", icon: ShieldCheck },
  { to: "/profile", label: "Profile", icon: Settings },
];

const adminMobileTabs = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin-governance", label: "Governance", icon: Activity },
  { to: "/admin/users", label: "Users", icon: Users },
  { to: "/audit", label: "Audit", icon: Shield },
  { to: "/profile", label: "Profile", icon: Settings },
];

function NavItem({ item, collapsed }) {
  const Ic = item.icon;
  const link = (
    <NavLink
      to={item.to}
      end={item.end ?? false}
      title={collapsed ? item.label : undefined}
      data-testid={`side-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
      className={({ isActive }) =>
        `flex items-center gap-3 rounded-md px-3 py-2.5 text-sm font-medium transition-colors ${
          collapsed ? "justify-center px-0" : ""
        } ${
          isActive
            ? "bg-primary text-primary-foreground shadow-sm"
            : "text-muted-foreground hover:text-foreground hover:bg-muted"
        }`
      }
    >
      <Ic className="h-4 w-4 shrink-0" aria-hidden="true" />
      {!collapsed && <span>{item.label}</span>}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{link}</TooltipTrigger>
        <TooltipContent side="right">{item.label}</TooltipContent>
      </Tooltip>
    );
  }
  return link;
}

function SidebarNavGroup({ group, collapsed }) {
  return (
    <div>
      {group.label && !collapsed && (
        <div className="px-3 pt-4 pb-1 text-[10px] uppercase tracking-[0.18em] text-muted-foreground/60 font-semibold select-none">
          {group.label}
        </div>
      )}
      {group.label && collapsed && <div className="pt-4" />}
      <div className="space-y-0.5">
        {group.items.map((item) => (
          <NavItem key={item.to} item={item} collapsed={collapsed} />
        ))}
      </div>
    </div>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cmdOpen, setCmdOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("crs_sidebar_collapsed") === "true"
  );

  const toggleSidebar = () => {
    setSidebarCollapsed((v) => {
      localStorage.setItem("crs_sidebar_collapsed", String(!v));
      return !v;
    });
  };

  const groups =
    user?.role === "admin" ? adminGroups
    : user?.role === "ca" ? caGroups
    : taxpayerGroups;

  const mobileTabs =
    user?.role === "admin" ? adminMobileTabs
    : user?.role === "ca" ? caMobileTabs
    : taxpayerMobileTabs;

  const allLinks = groups.flatMap((g) => g.items);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setCmdOpen((v) => !v);
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);

  if (!user) return null;

  return (
    <TooltipProvider delayDuration={300}>
      <div className="min-h-screen flex bg-muted/30">
        {/* ── Desktop sidebar ── */}
        <aside
          data-testid="sidebar"
          className={`hidden lg:flex shrink-0 flex-col border-r border-border bg-card overflow-hidden transition-[width] duration-200 ease-in-out ${
            sidebarCollapsed ? "w-14" : "w-64"
          }`}
        >
          {/* Logo */}
          <div className={`h-16 flex items-center border-b border-border shrink-0 ${sidebarCollapsed ? "justify-center px-0" : "px-5"}`}>
            <Link to="/" className="flex items-center gap-2.5 min-w-0">
              <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-primary text-primary-foreground font-display font-bold">
                R
              </div>
              {!sidebarCollapsed && (
                <div className="leading-tight min-w-0">
                  <div className="font-display text-[15px] font-bold tracking-tight truncate">CA Ritika Singh</div>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground capitalize">
                    {user.role}
                  </div>
                </div>
              )}
            </Link>
          </div>

          {/* Nav */}
          <nav
            className={`flex-1 overflow-y-auto py-3 space-y-0 ${sidebarCollapsed ? "px-1" : "px-3"}`}
            data-testid="sidebar-nav"
            aria-label="Sidebar navigation"
          >
            {groups.map((group, i) => (
              <SidebarNavGroup key={i} group={group} collapsed={sidebarCollapsed} />
            ))}
          </nav>

          {/* User footer */}
          <div className={`border-t border-border shrink-0 ${sidebarCollapsed ? "p-2" : "p-4"}`}>
            {!sidebarCollapsed && (
              <div className="flex items-center gap-3 mb-3">
                <Avatar className="h-9 w-9 shrink-0">
                  <AvatarImage src={user.picture || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                    {user.full_name?.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </AvatarFallback>
                </Avatar>
                <div className="min-w-0 leading-tight">
                  <div className="truncate text-sm font-semibold">{user.full_name}</div>
                  <div className="truncate text-xs text-muted-foreground">{user.email}</div>
                </div>
              </div>
            )}
            {sidebarCollapsed ? (
              <div className="flex flex-col items-center gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src={user.picture || undefined} />
                  <AvatarFallback className="bg-primary/10 text-primary text-xs font-semibold">
                    {user.full_name?.split(" ").map((p) => p[0]).slice(0, 2).join("")}
                  </AvatarFallback>
                </Avatar>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8"
                  onClick={toggleSidebar}
                  aria-label="Expand sidebar"
                  data-testid="sidebar-collapse-toggle"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  onClick={() => { logout(); navigate("/"); }}
                  variant="outline"
                  className="flex-1"
                  data-testid="shell-logout"
                >
                  <LogOut className="h-4 w-4 mr-2" aria-hidden="true" />
                  Sign out
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="shrink-0"
                  onClick={toggleSidebar}
                  aria-label="Collapse sidebar"
                  data-testid="sidebar-collapse-toggle"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
              </div>
            )}
          </div>
        </aside>

        {/* ── Main area ── */}
        <main className="flex-1 min-w-0 flex flex-col">
          {/* Desktop top bar */}
          <div className="hidden lg:flex h-14 px-6 border-b border-border bg-card items-center justify-end gap-1 sticky top-0 z-30 shrink-0">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCmdOpen(true)}
              className="gap-2 text-muted-foreground text-sm font-normal mr-1"
              data-testid="search-trigger"
              aria-label="Search (⌘K)"
            >
              <Search className="h-4 w-4" aria-hidden="true" />
              <span className="hidden xl:inline">Search</span>
              <kbd className="hidden xl:inline pointer-events-none select-none rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground leading-none">
                ⌘K
              </kbd>
            </Button>
            <ThemeToggle />
            <NotificationsBell />
          </div>

          {/* Mobile top bar */}
          <div className="lg:hidden h-14 px-4 border-b border-border bg-card flex items-center justify-between sticky top-0 z-30 shrink-0">
            <Link to="/" className="flex items-center gap-2">
              <div className="grid h-8 w-8 place-items-center rounded-md bg-primary text-primary-foreground font-display font-bold text-sm">
                R
              </div>
              <span className="font-display font-bold">CA Ritika Singh</span>
            </Link>
            <div className="flex items-center gap-0.5">
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setCmdOpen(true)}
                aria-label="Search"
                data-testid="mobile-search-trigger"
              >
                <Search className="h-4 w-4" aria-hidden="true" />
              </Button>
              <ThemeToggle />
              <NotificationsBell />
            </div>
          </div>

          {/* Page content */}
          <div className="flex-1 px-4 sm:px-6 lg:px-10 py-6 lg:py-8 pb-20 lg:pb-8">
            {children}
          </div>

          {/* Mobile bottom tab bar */}
          <nav
            className="lg:hidden fixed bottom-0 inset-x-0 z-40 border-t border-border bg-card flex safe-area-inset-bottom"
            aria-label="Primary navigation"
            data-testid="mobile-bottom-nav"
          >
            {mobileTabs.map((tab) => {
              const Ic = tab.icon;
              return (
                <NavLink
                  key={tab.to}
                  to={tab.to}
                  end={tab.end ?? false}
                  className={({ isActive }) =>
                    `flex-1 flex flex-col items-center justify-center gap-0.5 py-2.5 text-[10px] font-medium transition-colors ${
                      isActive ? "text-primary" : "text-muted-foreground"
                    }`
                  }
                  aria-label={tab.label}
                >
                  <Ic className="h-5 w-5" aria-hidden="true" />
                  <span>{tab.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </main>

        {/* Global overlays */}
        <MobileFAB />
        <AIAssistantWidget />
        <CommandPalette open={cmdOpen} onOpenChange={setCmdOpen} links={allLinks} />
      </div>
    </TooltipProvider>
  );
}
